import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Star, CheckCircle, ChevronRight, Facebook, MapPinned, CreditCard, Banknote, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ServicesSection from '../components/ServicesSection';
import GallerySection from '../components/GallerySection';
import ContactForm from '../components/ContactForm';
import QuoteForm from '../components/QuoteForm';
import AppointmentForm from '../components/AppointmentForm';
import TestimonialsSection from '../components/TestimonialsSection';
import { contactInfo } from '../data/mock';

const Home = () => {
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  const handleServiceClick = (serviceName) => {
    setSelectedService(serviceName);
    setShowQuoteForm(true);
  };

  const handleCloseQuoteForm = () => {
    setShowQuoteForm(false);
    setSelectedService('');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_clean-cars-alsace/artifacts/2ue0aq63_1.jpeg" 
                alt="Clean Cars Alsace Logo" 
                className="h-16 w-auto object-contain"
              />
            </div>
            <nav className="hidden md:flex items-center space-x-6">
              <a href="#services" className="text-gray-700 hover:text-blue-500 transition-colors font-medium blue-underline">Services</a>
              <a href="#gallery" className="text-gray-700 hover:text-blue-500 transition-colors font-medium blue-underline">Galerie</a>
              <a href="#testimonials" className="text-gray-700 hover:text-blue-500 transition-colors font-medium blue-underline">Avis</a>
              <a href="#contact" className="text-gray-700 hover:text-blue-500 transition-colors font-medium blue-underline">Contact</a>
              <a href={`tel:${contactInfo.phone}`}>
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold shadow-lg hover:shadow-xl transition-all">
                  <Phone className="w-4 h-4 mr-2" />
                  {contactInfo.phone}
                </Button>
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 z-0"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-yellow-100 rounded-full border border-blue-200">
                <span className="text-blue-700 font-semibold text-sm">🌟 Service disponible 24h/24 - 7j/7</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                Nettoyage <span className="bg-gradient-to-r from-yellow-500 to-blue-500 bg-clip-text text-transparent">Professionnel</span> en Alsace
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Votre partenaire de confiance pour le nettoyage automobile, terrasses, murets, portails, canapés et tapis. 
                Des résultats impeccables garantis.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                  onClick={() => setShowQuoteForm(true)}
                >
                  Demander un Devis
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-gray-300 text-gray-900 font-semibold text-lg px-8 py-6 hover:bg-gray-50 transition-all"
                  onClick={() => setShowAppointmentForm(true)}
                >
                  Prendre RDV
                </Button>
              </div>
              <div className="flex items-center space-x-8 pt-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-6 h-6 text-yellow-500" />
                  <span className="text-gray-700 font-medium">Toute l'Alsace</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-6 h-6 text-yellow-500" />
                  <span className="text-gray-700 font-medium">Devis Gratuit</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-6 h-6 text-yellow-500" />
                  <span className="text-gray-700 font-medium">Résultats Garantis</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_clean-cars-alsace/artifacts/bvd7e037_IMG_6273.jpeg" 
                  alt="Clean Cars Alsace - Nettoyage professionnel" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-blue-500 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">5.0</p>
                    <p className="text-sm text-gray-600">Avis Clients</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Bar */}
      <section className="bg-gradient-to-r from-yellow-500 via-yellow-400 to-blue-400 py-6 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-900">
            <a href={`tel:${contactInfo.phone}`} className="flex items-center space-x-2 hover:scale-105 transition-transform">
              <Phone className="w-5 h-5" />
              <span className="font-semibold">{contactInfo.phone}</span>
            </a>
            <a href={`mailto:${contactInfo.email}`} className="flex items-center space-x-2 hover:scale-105 transition-transform">
              <Mail className="w-5 h-5" />
              <span className="font-semibold">{contactInfo.email}</span>
            </a>
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5" />
              <span className="font-semibold">{contactInfo.hours}</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5" />
              <span className="font-semibold">{contactInfo.serviceArea}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <ServicesSection onServiceClick={handleServiceClick} />

      {/* Gallery Section */}
      <GallerySection />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Notre Localisation</h2>
            <p className="text-xl text-gray-600">Retrouvez-nous à Sélestat, Route de Colmar</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2660.5!2d7.4520!3d48.2590!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDE1JzMyLjQiTiA3wrAyNycwNy4yIkU!5e0!3m2!1sfr!2sfr!4v1234567890&q=Route+de+Colmar+Selestat+67600"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Clean Cars Alsace - Route de Colmar, Sélestat"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Contactez-Nous</h2>
            <p className="text-xl text-gray-600">Une question ? Un projet ? N'hésitez pas à nous contacter</p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_clean-cars-alsace/artifacts/2ue0aq63_1.jpeg" 
                  alt="Clean Cars Alsace Logo" 
                  className="h-20 w-auto object-contain"
                />
              </div>
              <p className="text-gray-400 leading-relaxed mb-4">
                Votre partenaire de confiance pour tous vos besoins de nettoyage professionnel en Alsace.
              </p>
              <p className="text-xs text-gray-500">
                SIRET : 941 835 100 00012
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4 text-yellow-400">Services</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Nettoyage Automobile</li>
                <li>Terrasses & Murets</li>
                <li>Portails</li>
                <li>Canapés & Fauteuils</li>
                <li>Tapis & Moquettes</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4 text-yellow-400">Infos Pratiques</h3>
              <div className="space-y-3 text-gray-400 text-sm">
                <div>
                  <p className="font-semibold text-white mb-1">Frais kilométriques :</p>
                  <p>15km offerts depuis Sélestat</p>
                  <p>Puis 0,50€/km</p>
                </div>
                <div>
                  <p className="font-semibold text-white mb-1">Moyens de paiement :</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="flex items-center">
                      <CreditCard className="w-4 h-4 mr-1" /> CB
                    </span>
                    <span className="flex items-center">
                      <Smartphone className="w-4 h-4 mr-1" /> Virement
                    </span>
                    <span className="flex items-center">
                      <Banknote className="w-4 h-4 mr-1" /> Espèces
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4 text-yellow-400">Contact</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>{contactInfo.phone}</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>{contactInfo.email}</span>
                </li>
                <li className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>{contactInfo.address}</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>{contactInfo.hours}</span>
                </li>
              </ul>
              <div className="mt-4">
                <p className="text-sm font-semibold text-yellow-400 mb-2">Suivez-nous :</p>
                <div className="flex space-x-3">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61573968245617" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a 
                    href="https://maps.app.goo.gl/ofFJrZHV5CsK8F357" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors"
                  >
                    <MapPinned className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Clean Cars Alsace. Tous droits réservés.</p>
          </div>
        </div>
      </footer>

      {/* Quote Modal */}
      {showQuoteForm && (
        <QuoteForm onClose={handleCloseQuoteForm} preSelectedService={selectedService} />
      )}

      {/* Appointment Modal */}
      {showAppointmentForm && (
        <AppointmentForm onClose={() => setShowAppointmentForm(false)} />
      )}
    </div>
  );
};

export default Home;
